import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useLocation } from "react-router";
import { CATEGORIES_TYPES, images, constants } from "common";
import {
  deleteUserAndData,
  getDashboardStats,
  getUsersByCompany,
} from "services/firebaseServices";
import { getProfile } from "utilities";
import { collection, getDocs, query, where } from "firebase/firestore";
import { fireStoreDB } from "services";
import { GenericModal } from "components";
import AddUser from "../components/AddUser";
import DutyOfCareSummary from "../components/DutyOfCareSummary";
import { Modal } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.css";
import { ApplicationContext } from "shared";

export default function BusinessDashboardComponent({ onNavigate }) {
  const route = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [users, setUsers] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // 🔥 Fetch business info from Firestore

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const formattedName = businessData.companyName
      .toLowerCase()
      .replace(/\s+/g, "-");
    const url = `${constants.siteBaseURL}/${formattedName}/sign-up`;

    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // Tooltip hides after 1.5s
    });
  };
  const handleDeleteConfirm = async () => {
    console.log("Item deleted!", showDeleteModal);
    // setShowDeleteModal(false);

    let res = deleteUserAndData(showDeleteModal);
    if (res) {
      fetchUsers();
      setShowDeleteModal(false);
    }
    // 🔥 Call your delete API or Firestore delete logic here
  };
  const { categoryListing, setCategoryListing } =
    useContext(ApplicationContext);
  const fetchRecords = async (users) => {
    try {
      const tempData = [...categoryListing];
      if (!!users) {
        tempData[0].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.diagnoses,
          users,
        );
        tempData[1].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.medicines,
          users,
        );
        tempData[2].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.allergies,
          users,
        );
        tempData[3].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.vaccines,
          users,
        );
      }
      setCategoryListing(tempData);
    } catch (err) {
      console.log(err);
    }
  };
  const fetchBusinessInfo = async () => {
    try {
      const profile = getProfile();
      if (!profile?.uid) return;

      const q = query(
        collection(fireStoreDB, "businesses"),
        where("userId", "==", profile.uid),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setBusinessData(snapshot.docs[0].data());
      }
    } catch (error) {
      console.error("Error fetching business info:", error);
    }
  };

  useEffect(() => {
    fetchBusinessInfo();
  }, []);
  const fetchUsers = async () => {
    try {
      const result = await getUsersByCompany(businessData?.companyName);
      setUsers(result);
      fetchRecords(result);
    } catch (error) {
      console.error("Error fetching company users:", error);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, [businessData?.companyName]); // 👈 dependency in case companyName changes
  const handleNavigation = (path) => {
    route.push(path);
    setMobileOpen(false);
  };

  const categoryColors = ["#EA4FC6", "#67CBFF", "#FFD36A", "#7B74FF"];
  const categoryNames = categoryListing.map((item) => item.mainTitle || "");
  const categoryValues = categoryListing.map((item) => item.dataListing?.length || 0);

  const healthScoreOptions = {
    chart: {
      type: "column",
      height: 260,
      backgroundColor: "transparent",
      style: { fontFamily: "Inter, sans-serif" },
    },
    title: { text: null },
    xAxis: {
      categories: categoryNames,
      labels: {
        style: { color: "#6b7280", fontSize: "13px", fontWeight: "500" },
      },
      lineColor: "#e5e7eb",
      tickLength: 0,
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      title: { text: null },
      labels: {
        style: { color: "#9ca3af", fontSize: "11px" },
      },
      gridLineColor: "#f3f4f6",
    },
    tooltip: {
      headerFormat: "",
      pointFormat: "<b>{point.category}</b>: {point.y} records",
      backgroundColor: "#1f2937",
      borderWidth: 0,
      borderRadius: 8,
      style: { color: "#fff", fontSize: "13px" },
    },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 6,
        pointPadding: 0.1,
        groupPadding: 0.15,
        dataLabels: {
          enabled: true,
          style: { color: "#374151", fontWeight: "600", fontSize: "13px", textOutline: "none" },
        },
      },
    },
    series: [
      {
        name: "Records",
        data: categoryValues.map((val, i) => ({ y: val, color: categoryColors[i] })),
      },
    ],
  };

  const dayWiseCounts = {};

  categoryListing.forEach((category) => {
    category.dataListing.forEach((entry) => {
      const date = entry.date || "Unknown";
      dayWiseCounts[date] = (dayWiseCounts[date] || 0) + 1;
    });
  });

  const metrics = [
    {
      label: "Diagnoses reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Diagnoses")
          ?.dataListing?.length || 0,
      color: "#EA4FC640",
      icon: images.info,
    },
    {
      label: "Medications reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Medicines")
          ?.dataListing?.length || 0,
      color: "#67CBFF40",
      icon: images.scale,
    },
    {
      label: "Allergies Reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Allergies")
          ?.dataListing?.length || 0,
      color: "#FFD36A40",
      icon: images.cup,
    },
    {
      label: "Vaccines reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Vaccines")
          ?.dataListing?.length || 0,
      color: "#7B74FF40",
      icon: images.pill,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: { xs: 1, sm: 0 } }}
        >
          Dashboard
        </Typography>
      </Box>

      {/* Metrics Row */}
      <Grid container spacing={2} mb={3}>
        {metrics.map((m, i) => (
          <Grid item xs={12} sm={6} md={3} key={m.label}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: m.color,
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <img
                  src={m.icon}
                  style={{ width: '22px', borderRadius: 0 }}
                  alt={m.label}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                {m.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1 }}>
                {m.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Duty of Care Readiness */}
      <DutyOfCareSummary
        users={users}
        loading={!businessData}
        onViewDetails={onNavigate ? () => onNavigate("compliance") : undefined}
      />

      {/* Employee Overview Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #e5e7eb',
          p: 3,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography fontWeight={700} variant="h6" sx={{ color: "#101828" }}>
              Employee Overview
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085" }}>
              {users?.length || 0} {users?.length === 1 ? "member" : "members"}
            </Typography>
          </Box>
          <Tooltip
            title={copied ? "Link copied!" : "Copy sign-up link"}
            open={copied}
            onClose={() => setCopied(false)}
            disableHoverListener
            arrow
          >
            <Button
              variant="outlined"
              startIcon={<IosShareIcon sx={{ fontSize: 18 }} />}
              onClick={handleCopy}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                px: 2,
                py: 0.9,
                color: "#0B6FB0",
                borderColor: "#CDE9FB",
                bgcolor: "#F5FBFF",
                "&:hover": { borderColor: "#32ABF8", bgcolor: "#EAF6FE" },
              }}
            >
              Share sign-up link
            </Button>
          </Tooltip>
        </Box>
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          backdrop="static"
          keyboard={false}
          enforceFocus={false}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="generic-modal-layout"
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "20px",
              paddingTop: "0px",
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            <Modal.Body>
              <AddUser
                setShowModal={setShowModal}
                companyName={businessData?.companyName}
                fetchBusinessInfo={fetchUsers}
              />
            </Modal.Body>
          </div>
        </Modal>

        <GenericModal
          show={!!showDeleteModal}
          type={4} // type 4 = “Are you sure?” style
          title="Confirm Delete"
          body="Are you sure you want to delete this record? This action cannot be undone."
          buttonText="Cancel"
          buttonText2="Delete"
          grayButton // makes cancel gray
          button2={true}
          grayButton2={false} // makes delete primary
          // handleClose={() => setShowDeleteModal(false)}
          primaryBtnPress={() => setShowDeleteModal(false)} // Cancel
          secondaryBtnPress={handleDeleteConfirm} // Delete confirmed
        />
        {/* Responsive Table */}
        <Box sx={{ overflow: "auto", border: "1px solid #EAECF0", borderRadius: 3 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                {["Name", "Email", "Member Since", ""].map((h, idx) => (
                  <TableCell
                    key={h || idx}
                    align={idx === 3 ? "right" : "left"}
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid #EAECF0",
                      color: "#667085",
                      fontWeight: 600,
                      fontSize: 12,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(!users || users.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 5, color: "#98A2B3", border: 0 }}>
                    No employees yet. Share your sign-up link to get started.
                  </TableCell>
                </TableRow>
              )}
              {users.map((row, i) => {
                const initials = `${(row.firstName || "")[0] || ""}${(row.lastName || "")[0] || ""}`.toUpperCase();
                return (
                  <TableRow
                    key={i}
                    sx={{ "&:hover": { bgcolor: "#F9FAFB" }, "&:last-child td": { border: 0 } }}
                  >
                    <TableCell sx={{ borderBottom: "1px solid #F2F4F7", py: 1.25 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            bgcolor: "#EAF6FE",
                            color: "#0B6FB0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12.5,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {initials || "–"}
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#101828" }}>
                          {row.firstName} {row.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F2F4F7", color: "#475467", fontSize: 14 }}>
                      {row.email}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F2F4F7", color: "#475467", fontSize: 14 }}>
                      {row.memberSince || "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "1px solid #F2F4F7" }}>
                      <Tooltip title="Remove employee" arrow>
                        <IconButton
                          onClick={() => setShowDeleteModal(row.id)}
                          size="small"
                          sx={{ color: "#98A2B3", "&:hover": { color: "#D92D20", bgcolor: "#FEF3F2" } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
